import mapboxgl from "mapbox-gl";

// Coordinates match the Python script's convention (what the Worker expects).
export interface BoundingBox {
  lat_min: number;
  lat_max: number;
  lon_min: number;
  lon_max: number;
}

/** The four visible-frame corners, each as [lng, lat], in screen-pixel
 *  order: NW (top-left), NE (top-right), SE (bottom-right), SW (bottom-left).
 *  When bearing=0 these describe an axis-aligned rect equal to
 *  BoundingBox; with bearing!=0 they describe the actual rotated
 *  rectangle the user sees in the frame. Python uses them to extract
 *  the rotated rect from the axis-aligned stitched download. */
export type FrameCorners = {
  nw: [number, number];
  ne: [number, number];
  se: [number, number];
  sw: [number, number];
};

export interface MapHandle {
  map: mapboxgl.Map;
  getBounds: () => BoundingBox;
  getFrameCorners: () => FrameCorners;
  onBoundsChange: (cb: (bbox: BoundingBox) => void) => void;
  resize: () => void;
  setLandmarkDisplays: (displays: LandmarkDisplay[]) => void;
  /** Fires when the user drags a landmark text label. The offset is in
   *  pixels from the landmark's true lat/lon anchor. */
  onMarkerMoved: (cb: (id: string, offset: [number, number]) => void) => void;
  setLabelsVisible: (visible: boolean) => void;
  setLocked: (locked: boolean) => void;
  setTone: (tone: Partial<PosterTone>) => void;
}

/**
 * Unified marker description covering all landmark display modes.
 *   "text"         — full red uppercase label at position (current default)
 *   "dot-red"      — small red numbered circle (top-10 famous preview)
 *   "dot-yellow"   — small yellow numbered circle (rest of preview)
 *   "dot-selected" — red numbered circle with white ring (selected, legend mode)
 */
export interface LandmarkDisplay {
  id: string;
  name: string;
  lat: number;
  lon: number;
  style: "text" | "dot-red" | "dot-yellow" | "dot-selected";
  number?: number;
  /** Pixel offset from the landmark's true lat/lon anchor. Used to
   *  manually move overlapping labels apart while keeping the geographic
   *  anchor intact. */
  offsetPx?: [number, number];
}

export interface PosterTone {
  brightnessMin: number; // 0..1
  brightnessMax: number; // 0..1
  contrast: number; // -1..1
  saturation: number; // -1..1
}

export const DEFAULT_POSTER_TONE: PosterTone = {
  brightnessMin: 0.08,
  brightnessMax: 1.0,
  contrast: -0.12,
  saturation: -0.1,
};

const STYLE_PLAIN = "mapbox://styles/mapbox/satellite-v9"; // no labels (matches poster)
const STYLE_LABELLED = "mapbox://styles/mapbox/satellite-streets-v12"; // labels + roads

/**
 * Poster tone: lifts shadows + softens contrast on the satellite raster so
 * the preview matches the IKONOSPACE reference (lighter, more "print-like"
 * tones vs. Mapbox's default deeper saturation). Applied via Mapbox's
 * raster paint properties — no CSS filter needed, so Markers (landmark
 * labels, poster title) stay unaffected automatically.
 *
 * Values can be tweaked at runtime via MapHandle.setTone().
 */
function applyPosterTone(map: mapboxgl.Map, tone: PosterTone): void {
  // Called from setTone() which may run before the style has finished loading.
  // Bail out early; the map.on("style.load") handler will re-apply once ready.
  if (!map.isStyleLoaded()) return;
  const style = map.getStyle();
  if (!style?.layers) return;
  for (const layer of style.layers) {
    if (layer.type !== "raster") continue;
    try {
      map.setPaintProperty(layer.id, "raster-brightness-min", tone.brightnessMin);
      map.setPaintProperty(layer.id, "raster-brightness-max", tone.brightnessMax);
      map.setPaintProperty(layer.id, "raster-contrast", tone.contrast);
      map.setPaintProperty(layer.id, "raster-saturation", tone.saturation);
    } catch {
      /* layer may not accept these props; skip silently */
    }
  }
}

/** Finer-grained zoom buttons. Mapbox's built-in NavigationControl hardcodes
 *  a step of 1.0 per click, which doubles/halves the scale — too coarse for
 *  composing a poster frame. */
const ZOOM_STEP = 0.1;
const ZOOM_DURATION_MS = 180;

class FineZoomControl implements mapboxgl.IControl {
  private container!: HTMLElement;
  private map!: mapboxgl.Map;

  onAdd(map: mapboxgl.Map): HTMLElement {
    this.map = map;
    this.container = document.createElement("div");
    this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
    this.container.appendChild(this.makeButton("in"));
    this.container.appendChild(this.makeButton("out"));
    return this.container;
  }

  onRemove(): void {
    this.container.remove();
  }

  private makeButton(direction: "in" | "out"): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `mapboxgl-ctrl-zoom-${direction}`;
    const label = direction === "in" ? "Zoom in" : "Zoom out";
    btn.title = label;
    btn.setAttribute("aria-label", label);

    const icon = document.createElement("span");
    icon.className = "mapboxgl-ctrl-icon";
    icon.setAttribute("aria-hidden", "true");
    btn.appendChild(icon);

    btn.addEventListener("click", () => {
      const delta = direction === "in" ? ZOOM_STEP : -ZOOM_STEP;
      this.map.easeTo({
        zoom: this.map.getZoom() + delta,
        duration: ZOOM_DURATION_MS,
      });
    });
    return btn;
  }
}

/**
 * Poster-style scale bar: thin bar with four alternating segments, small
 * distance label underneath, and a minimal "N" compass dot to the right.
 * Matches the IKONOSPACE / The Design Office aesthetic referenced in docs.
 */
const SCALE_TARGET_PX = 120;

class PosterScaleControl implements mapboxgl.IControl {
  private container!: HTMLElement;
  private bar!: HTMLElement;
  private label!: HTMLElement;
  private map!: mapboxgl.Map;
  private onMove = () => this.update();

  onAdd(map: mapboxgl.Map): HTMLElement {
    this.map = map;

    this.container = document.createElement("div");
    this.container.className = "mapboxgl-ctrl poster-scale";

    const scaleGroup = document.createElement("div");
    scaleGroup.className = "poster-scale-group";

    this.bar = document.createElement("div");
    this.bar.className = "poster-scale-bar";

    this.label = document.createElement("div");
    this.label.className = "poster-scale-label";

    scaleGroup.appendChild(this.bar);
    scaleGroup.appendChild(this.label);

    const compass = document.createElement("div");
    compass.className = "poster-compass";
    compass.setAttribute("aria-label", "North");
    compass.textContent = "N";

    this.container.appendChild(scaleGroup);
    this.container.appendChild(compass);

    this.update();
    map.on("move", this.onMove);
    map.on("zoom", this.onMove);
    return this.container;
  }

  onRemove(): void {
    this.map.off("move", this.onMove);
    this.map.off("zoom", this.onMove);
    this.container.remove();
  }

  private update(): void {
    const lat = this.map.getCenter().lat;
    // Ground resolution formula (meters per pixel at `lat`, zoom z)
    const metersPerPx =
      (156543.03392 * Math.cos((lat * Math.PI) / 180)) /
      2 ** this.map.getZoom();

    const targetMeters = metersPerPx * SCALE_TARGET_PX;
    const niceMeters = niceRound(targetMeters);
    const pxWidth = Math.round(niceMeters / metersPerPx);

    this.bar.style.width = `${pxWidth}px`;
    this.label.textContent = formatDistance(niceMeters);
  }
}

function niceRound(n: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(n));
  const normalized = n / magnitude;
  let nice: number;
  if (normalized < 1.5) nice = 1;
  else if (normalized < 3) nice = 2;
  else if (normalized < 7) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

function formatDistance(m: number): string {
  if (m >= 1000) return `${m / 1000} km`;
  return `${m} m`;
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN || "";

export function initMap(container: HTMLElement): MapHandle {
  if (!MAPBOX_TOKEN) {
    container.textContent =
      "Missing VITE_MAPBOX_PUBLIC_TOKEN. Set it in the Cloudflare Pages env.";
    throw new Error("Missing Mapbox public token");
  }

  mapboxgl.accessToken = MAPBOX_TOKEN;

  const map = new mapboxgl.Map({
    container,
    // Pure satellite (no labels, no roads) — matches the Python script.
    style: STYLE_PLAIN,
    center: [-8.6108, 41.1551], // Porto default
    zoom: 13.5,
    attributionControl: false,
  });

  const navControl = new FineZoomControl();
  map.addControl(navControl, "top-right");
  // Mapbox's built-in compass: click the ▲ to reset north, or drag/right-drag
  // the map to rotate. Rotation IS baked into the poster now (Python
  // extracts the rotated rect via Image.transform QUAD), so the compass
  // is a real feature, not preview-only.
  const compassControl = new mapboxgl.NavigationControl({
    showZoom: false,
    showCompass: true,
    visualizePitch: false,
  });
  map.addControl(compassControl, "top-right");
  map.addControl(new PosterScaleControl(), "bottom-right");
  // Attribution moves to bottom-left so it doesn't crowd the poster scale bar.
  map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-left");

  // Bounds from the ACTUAL visible frame. We read #map's own size (the
  // container we passed to new Map({container}) — guaranteed to match
  // preview-inner because of width/height: 100% in styles.css), NOT
  // getCanvasContainer() (which is Mapbox's internal div that can have
  // different sizing semantics) and NOT map.getBounds() (documented to
  // return a larger bbox when bearing != 0 and in practice observed to
  // over-report even at bearing 0).
  //
  // unproject(px) converts pixel coords → lat/lon accounting for
  // current bearing/pitch. The tight axis-aligned bbox of the four
  // corner unprojections is exactly what the user sees in the frame.
  const currentBounds = (): BoundingBox => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (!w || !h) {
      const b = map.getBounds();
      if (!b) return { lat_min: 0, lat_max: 0, lon_min: 0, lon_max: 0 };
      return {
        lat_min: b.getSouth(),
        lat_max: b.getNorth(),
        lon_min: b.getWest(),
        lon_max: b.getEast(),
      };
    }
    const corners = [
      map.unproject([0, 0]),
      map.unproject([w, 0]),
      map.unproject([w, h]),
      map.unproject([0, h]),
    ];
    const lats = corners.map((c) => c.lat);
    const lngs = corners.map((c) => c.lng);
    const bbox = {
      lat_min: Math.min(...lats),
      lat_max: Math.max(...lats),
      lon_min: Math.min(...lngs),
      lon_max: Math.max(...lngs),
    };
    // Dev-visibility log so the bbox source is obvious in the console.
    // Remove once bbox mismatch is confirmed resolved in production.
    console.debug("[bbox]", `${w}×${h}px`, "→", bbox);
    return bbox;
  };

  const listeners: Array<(bbox: BoundingBox) => void> = [];
  map.on("moveend", () => {
    const bbox = currentBounds();
    listeners.forEach((cb) => cb(bbox));
  });
  let currentTone: PosterTone = { ...DEFAULT_POSTER_TONE };

  map.on("load", () => {
    applyPosterTone(map, currentTone);
    listeners.forEach((cb) => cb(currentBounds()));
  });
  // `style.load` fires after both the initial load AND subsequent setStyle().
  map.on("style.load", () => applyPosterTone(map, currentTone));

  // ─── Landmark markers ────────────────────────────────────────────────
  // Keyed by id; each entry remembers its display signature so we only
  // re-create a marker when its style/number actually changes (avoids
  // flicker on pan where the same landmark is rendered identically).
  interface ActiveMarker {
    marker: mapboxgl.Marker;
    sig: string;
    currentOffset: [number, number];
  }
  const activeMarkers = new Map<string, ActiveMarker>();
  const markerMovedListeners: Array<(id: string, offset: [number, number]) => void> = [];

  /**
   * Pointer-based drag for label repositioning. The marker's LngLat stays
   * fixed (the geographic anchor is semantic), but its pixel offset moves —
   * so the user can drag overlapping labels apart without losing the
   * meaning of "this label belongs to that point on the map".
   *
   * We can't use mapboxgl.Marker({ draggable: true }) here because that
   * mutates the LngLat; we want LngLat locked and only the visual offset
   * to change. So we intercept pointer events on the element and call
   * marker.setOffset() directly.
   */
  function attachDragBehavior(
    el: HTMLElement,
    marker: mapboxgl.Marker,
    active: ActiveMarker,
    id: string,
  ) {
    el.style.cursor = "grab";
    el.style.touchAction = "none"; // prevent scroll-steal on touch drag
    el.style.pointerEvents = "auto";

    let dragStart: { x: number; y: number; ox: number; oy: number } | null = null;
    let moved = false;

    el.addEventListener("pointerdown", (e) => {
      // Let clicks on a dot for selection pass through — drag kicks in only
      // after >3px of movement. For now, begin drag on any pointerdown.
      e.stopPropagation();
      e.preventDefault();
      dragStart = {
        x: e.clientX,
        y: e.clientY,
        ox: active.currentOffset[0],
        oy: active.currentOffset[1],
      };
      moved = false;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = "grabbing";
    });

    el.addEventListener("pointermove", (e) => {
      if (!dragStart) return;
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      if (!moved && Math.hypot(dx, dy) < 3) return;
      moved = true;
      const newOffset: [number, number] = [dragStart.ox + dx, dragStart.oy + dy];
      marker.setOffset(newOffset);
      active.currentOffset = newOffset;
    });

    const endDrag = (e: PointerEvent) => {
      if (!dragStart) return;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer already released */
      }
      el.style.cursor = "grab";
      dragStart = null;
      if (moved) {
        for (const cb of markerMovedListeners) cb(id, [...active.currentOffset]);
      }
    };
    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);

    // Double-click resets the label to its geographic anchor (offset 0,0).
    el.addEventListener("dblclick", (e) => {
      e.stopPropagation();
      e.preventDefault();
      marker.setOffset([0, 0]);
      active.currentOffset = [0, 0];
      for (const cb of markerMovedListeners) cb(id, [0, 0]);
    });
    el.title = el.title || "Drag to reposition · double-click to reset";
  }

  const setLandmarkDisplays = (displays: LandmarkDisplay[]) => {
    const wantedIds = new Set(displays.map((d) => d.id));

    // Remove markers no longer wanted.
    for (const [id, entry] of activeMarkers) {
      if (!wantedIds.has(id)) {
        entry.marker.remove();
        activeMarkers.delete(id);
      }
    }

    // Add or update.
    for (const d of displays) {
      const offset = d.offsetPx ?? [0, 0];
      // Signature includes offset so mid-drag re-renders don't thrash the marker.
      const sig = `${d.style}|${d.number ?? ""}|${d.name}|${offset[0]},${offset[1]}`;
      const existing = activeMarkers.get(d.id);
      if (existing && existing.sig === sig) continue;
      // Always recreate when anything changes (including offset). The
      // previous in-place setOffset() shortcut didn't reliably trigger a
      // visual repaint, which made the ↺ reset button appear to do nothing.
      if (existing) {
        existing.marker.remove();
        activeMarkers.delete(d.id);
      }

      const el = document.createElement("div");
      if (d.style === "text") {
        el.className = "landmark-marker";
        el.textContent = d.name.toUpperCase();
      } else {
        el.className = `landmark-dot ${d.style}`;
        if (d.number !== undefined) el.textContent = String(d.number);
        el.title = d.name;
      }

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: "center",
        offset,
      })
        .setLngLat([d.lon, d.lat])
        .addTo(map);

      const active: ActiveMarker = { marker, sig, currentOffset: [...offset] };
      activeMarkers.set(d.id, active);

      // Only the selected visuals (text / dot-selected) are draggable.
      // Unselected preview dots stay as positional markers and don't need
      // repositioning.
      if (d.style === "text" || d.style === "dot-selected") {
        attachDragBehavior(el, marker, active, d.id);
      }
    }
  };

  const setLabelsVisible = (visible: boolean) => {
    map.setStyle(visible ? STYLE_LABELLED : STYLE_PLAIN);
    // Style swap does not remove HTML Markers — they stay positioned.
  };

  // Track whether nav controls are currently on the map so we never
  // double-add (Mapbox doesn't throw on duplicate addControl — it silently
  // adds another DOM instance, which is why the buttons were duplicating).
  let controlsAdded = true;

  const setLocked = (locked: boolean) => {
    const interactions = [
      map.scrollZoom,
      map.boxZoom,
      map.dragPan,
      map.dragRotate,
      map.keyboard,
      map.doubleClickZoom,
      map.touchZoomRotate,
    ];
    for (const handler of interactions) {
      if (locked) handler.disable();
      else handler.enable();
    }
    if (locked && controlsAdded) {
      try { map.removeControl(navControl); } catch { /* */ }
      try { map.removeControl(compassControl); } catch { /* */ }
      controlsAdded = false;
    } else if (!locked && !controlsAdded) {
      map.addControl(navControl, "top-right");
      map.addControl(compassControl, "top-right");
      controlsAdded = true;
    }
  };

  const setTone = (update: Partial<PosterTone>) => {
    currentTone = { ...currentTone, ...update };
    applyPosterTone(map, currentTone);
  };

  const currentFrameCorners = (): FrameCorners => {
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    const asTuple = (ll: mapboxgl.LngLat): [number, number] => [ll.lng, ll.lat];
    return {
      nw: asTuple(map.unproject([0, 0])),
      ne: asTuple(map.unproject([w, 0])),
      se: asTuple(map.unproject([w, h])),
      sw: asTuple(map.unproject([0, h])),
    };
  };

  return {
    map,
    getBounds: currentBounds,
    getFrameCorners: currentFrameCorners,
    onBoundsChange: (cb) => listeners.push(cb),
    resize: () => map.resize(),
    setLandmarkDisplays,
    onMarkerMoved: (cb) => markerMovedListeners.push(cb),
    setLabelsVisible,
    setLocked,
    setTone,
  };
}

/**
 * Estimate tile count for a bbox + zoom. Mirrors Worker's `estimateTiles`.
 */
export function estimateTiles(bbox: BoundingBox, zoom: number): number {
  const n = 2 ** zoom;
  const xMin = Math.floor(((bbox.lon_min + 180) / 360) * n);
  const xMax = Math.floor(((bbox.lon_max + 180) / 360) * n);
  const latMaxRad = (bbox.lat_max * Math.PI) / 180;
  const latMinRad = (bbox.lat_min * Math.PI) / 180;
  const yMin = Math.floor(
    ((1 - Math.log(Math.tan(latMaxRad) + 1 / Math.cos(latMaxRad)) / Math.PI) / 2) * n,
  );
  const yMax = Math.floor(
    ((1 - Math.log(Math.tan(latMinRad) + 1 / Math.cos(latMinRad)) / Math.PI) / 2) * n,
  );
  return (xMax - xMin + 1) * (yMax - yMin + 1);
}
